import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type {
  ArtifactAttachmentRef,
  OneDriveAttachmentRef,
  OutgoingAttachment,
  SharePointAttachmentRef,
} from '@/integration/types.js';
import type { Skill } from '@/lib/api/skills.js';

/** Why `selectedWorkspaceId` last changed — only `'user'` should reset chat or hijack manage URLs. */
export type WorkspaceChangeReason = 'user' | 'system';

/** Bounds of the chat artifact panel, in pixels. Narrower hides a table; wider starves the chat. */
export const ARTIFACT_PANEL_MIN_WIDTH = 320;
export const ARTIFACT_PANEL_MAX_WIDTH = 720;
export const ARTIFACT_PANEL_DEFAULT_WIDTH = 420;

/** Clamp a raw (dragged or persisted) panel width; a non-finite value falls back to the minimum. */
function clampArtifactPanelWidth(rawWidth: number): number {
  if (!Number.isFinite(rawWidth)) return ARTIFACT_PANEL_MIN_WIDTH;
  return Math.min(
    ARTIFACT_PANEL_MAX_WIDTH,
    Math.max(ARTIFACT_PANEL_MIN_WIDTH, rawWidth),
  );
}

/**
 * UI store — chrome-only state (sidebar collapsed, mobile drawer open,
 * command palette open, context panel visible).
 *
 * Persists collapsed state across reloads so users with narrow viewports
 * don't have to re-collapse every session.
 */
interface UiState {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
  toggleSidebar: () => void;

  mobileDrawerOpen: boolean;
  setMobileDrawerOpen: (v: boolean) => void;

  commandPaletteOpen: boolean;
  setCommandPaletteOpen: (v: boolean) => void;
  toggleCommandPalette: () => void;

  contextPanelOpen: boolean;
  setContextPanelOpen: (v: boolean) => void;

  /**
   * How the Artifacts section renders its file list: `grid` (tiles, the default) or `table`.
   * Persisted so the choice survives reloads.
   */
  artifactsViewMode: 'grid' | 'table';
  setArtifactsViewMode: (mode: 'grid' | 'table') => void;
  /**
   * The person closed the offer to set Quality up in chat on `/quality`. Per browser, persisted, so
   * the offer is made once rather than on every visit.
   */
  qualitySetupOfferDismissed: boolean;
  setQualitySetupOfferDismissed: (dismissed: boolean) => void;
  /**
   * The person closed the "do it with the agent" tip on the Quality forms. Per browser, persisted;
   * the `/quality` header menu brings the tips back.
   */
  qualityAgentTipsDismissed: boolean;
  setQualityAgentTipsDismissed: (dismissed: boolean) => void;

  /**
   * Chat's right-hand artifact panel. All three fields are persisted: `activeArtifactId` is a durable
   * artifacts-registry id, so a reload restores the same file in the same panel at the same width.
   */
  artifactPanelOpen: boolean;
  activeArtifactId: string | null;
  artifactPanelWidth: number;
  /**
   * Monotonic count of explicit user closes. `useChat` snapshots it when a turn starts and compares on
   * the turn's first artifact, so a panel the user closed mid-turn is not reopened for that turn.
   * Ephemeral — never persisted; a reload starts a fresh baseline.
   */
  artifactPanelCloseCount: number;
  /**
   * The file the reader asked for by NAME in an answer — the one case where the panel may show a file
   * that is not one of this conversation's. Everything else the panel opens belongs to the chat it is
   * attached to, and an id that is not in the chat's list is a dead id there (the user switched chats,
   * or the file was deleted) which the panel replaces with the chat's newest file. Telling the two
   * apart is what keeps that rule intact while still letting a cited file open.
   *
   * Ephemeral — deliberately NOT persisted: a reload is not a request, so the restored panel goes back
   * to answering for the conversation alone.
   */
  requestedArtifactId: string | null;
  /** Open the panel on one artifact. Used by the turn's FIRST artifact and by the chat toolbar toggle. */
  openArtifactPanel: (artifactId: string) => void;
  /**
   * Open the panel on a file the answer named, which may belong to the workspace rather than to this
   * conversation. Same panel, same file; it only also records that this id was asked for.
   */
  openCitedArtifactPanel: (artifactId: string) => void;
  /**
   * Forget the request. The panel calls this when the conversation changes: a file named in one chat's
   * answer has no standing in another, so the panel goes back to answering for the chat it is in.
   */
  clearRequestedArtifact: () => void;
  closeArtifactPanel: () => void;
  /**
   * Drop the open flag WITHOUT counting a user close — for when the panel has nothing left to show
   * (the conversation's file list came back empty). It must not bump the close counter: that counter
   * means "the user dismissed this", and spending it here would suppress the auto-open for a file
   * arriving later in the same turn.
   */
  collapseArtifactPanel: () => void;
  /** Switch the shown file without touching the open flag or the close counter. */
  setActiveArtifactId: (artifactId: string) => void;
  /** Live drag update — the raw pointer-target width, clamped by the store. */
  setArtifactPanelWidth: (rawWidth: number) => void;

  /**
   * Currently selected global workspace (global-workspaces phase 2). Always a concrete workspace once
   * data loads — the "All workspaces" sentinel is gone; the switcher defaults `null` to the user's
   * personal workspace (see `resolveDefaultWorkspaceId`). Shared across every screen via the sidebar
   * switcher; persisted so the choice survives reloads.
   */
  selectedWorkspaceId: string | null;
  /**
   * Whether zustand/persist has finished rehydrating from localStorage. Route effects that react to
   * `selectedWorkspaceId` must wait for this before treating a change as meaningful — the id jumps
   * from the in-memory default (`null`) to the saved value during rehydration without a user action.
   */
  hasHydrated: boolean;
  /** Ephemeral — not persisted. Set by {@link setSelectedWorkspaceId}; rehydration leaves it `system`. */
  lastWorkspaceChangeReason: WorkspaceChangeReason;
  setSelectedWorkspaceId: (
    id: string | null,
    reason?: WorkspaceChangeReason,
  ) => void;

  /**
   * Files queued from the Home launcher. ThreadsPage Composer picks them up
   * on mount, adds them as attachments, then clears the store.
   * NOT persisted — session-only.
   */
  pendingFiles: File[];
  setPendingFiles: (files: File[]) => void;

  /** Ready-to-send attachments — uploads keyed by S3 key, or workspace-file references. Set from the
   *  Home Composer on submit. */
  pendingAttachments: OutgoingAttachment[];
  setPendingAttachments: (atts: OutgoingAttachment[]) => void;

  /**
   * Skill ids selected in a launcher composer (Home/Project) at submit time. Mirrors
   * `pendingAttachments`: /chats' useLayoutEffect adopts it into `chat.sendMessage`'s
   * `skillIds` opt, then clears it — without this carrier a chip picked before the
   * first send (which redirects through /chats) would silently never reach the
   * server. NOT persisted — session-only.
   */
  pendingSkillIds: string[];
  setPendingSkillIds: (ids: string[]) => void;

  /** Conversation id reserved by a launcher composer (Home/Project) so attachment uploads can be
   *  keyed to it before navigation. /chats adopts it as the target of the pending send so the
   *  message lands in the SAME conversation the files were uploaded under. Session-only. */
  pendingConversationId: string | null;
  setPendingConversationId: (id: string | null) => void;

  /** Set to true when mic is activated from the Home launcher. Threads Composer
   *  picks it up on mount, starts recording, then clears it. Session-only. */
  pendingMic: boolean;
  setPendingMic: (v: boolean) => void;

  /**
   * Draft text typed in the Home composer before the first keystroke redirect.
   * Passed via store so it never appears in the URL.
   * ThreadsPage reads it once on mount and clears it.
   */
  pendingDraft: string | null;
  setPendingDraft: (draft: string | null) => void;

  /**
   * One-shot prefill request for an already-mounted Composer. Distinct from
   * `pendingDraft` (which auto-sends on mount via /chats useLayoutEffect):
   * `composerPrefill` ONLY puts the text into the textarea so the user can
   * edit and click Send themselves. Consumed by useComposer's effect on
   * value change — the consumer clears it back to null so the same prefill
   * doesn't re-fire on every render.
   *
   * Used by the cancel flow's "Edit prompt" affordance.
   */
  composerPrefill: string | null;
  /**
   * Sets the prefill. With a `conversationId`, only a composer sending into that conversation takes
   * it; without one, any mounted composer does. Every call sets both, so no earlier target lingers.
   */
  setComposerPrefill: (
    text: string | null,
    conversationId?: string,
    options?: { keepDraft?: boolean },
  ) => void;

  /**
   * Whether the prefill goes in front of the composer's unsent text rather than replacing it, as Reply
   * to the Developer does with `#developer ` (MEDA-1212). Every `setComposerPrefill` call sets it.
   */
  composerPrefillKeepsDraft: boolean;

  /**
   * The conversation the prefill is meant for, or null when any composer may take it. A new chat
   * opened over the Home page needs it: the Home composer stays mounted until the chat page replaces
   * it, and it must not take a prefill meant for the chat's own composer.
   */
  composerPrefillConversationId: string | null;

  /** Whether the prefill carries a complete rejected launcher draft rather than text alone. */
  composerPrefillRestoresDraft: boolean;
  setComposerPrefillRestoresDraft: (restores: boolean) => void;

  /** Ready uploads and references restored with a rejected launcher draft. */
  composerPrefillAttachments: OutgoingAttachment[];
  setComposerPrefillAttachments: (attachments: OutgoingAttachment[]) => void;

  /** Skill chips restored with a rejected launcher draft. */
  composerPrefillSkillIds: string[];
  setComposerPrefillSkillIds: (ids: string[]) => void;

  /**
   * Skill queued from the directory modal's "Use" action (`ConnectorsDirectoryModal`
   * → `SkillsPane`'s onUseSkill). `useComposer` consumes it — adds it as a chip via
   * `addSkill` — then clears the slot. Mirrors the `pendingFiles` idiom. Session-only.
   */
  pendingSkill: Skill | null;
  setPendingSkill: (skill: Skill | null) => void;

  /**
   * A skill just created from a chat suggestion, waiting for the directory modal to open on its card.
   * Written by `SkillSuggestionSlot` when the editor saves, consumed and cleared by `ComposerPlusMenu`,
   * which already owns that modal.
   *
   * It travels through the store rather than being opened where it is requested, because accepting an
   * offer removes it from the conversation's list — which unmounts the very slot that asked, and any
   * modal it owned with it. Session-only: an accept is a gesture in flight, not a preference.
   */
  pendingSkillsDirectoryId: string | null;
  setPendingSkillsDirectoryId: (id: string | null) => void;

  /**
   * Workspace files queued for the composer, each already pinned to the revision it was picked at.
   * Every entry point outside the composer writes here — "Add to chat" in the Artifacts section (which
   * then navigates to the chat) and the `+` menu's picker — and `useComposer` consumes the slot into
   * chips and clears it. The chat's own file panel has no attach action: it only ever lists files of the
   * chat already on screen. Mirrors the `pendingSkill` idiom. Session-only, never persisted: a queued
   * file is a gesture in flight, not a preference.
   */
  pendingArtifactAttachments: ArtifactAttachmentRef[];
  /**
   * Which of {@link UiState.pendingArtifactAttachments} are private, by artifact id. Kept beside the
   * references instead of inside them: a reference is the exact six-key shape the server reads, while
   * this fact exists only so the composer can tell the user what attaching a private file to a shared
   * chat actually means. The same setter writes both, so they can never disagree.
   */
  pendingRestrictedArtifactIds: string[];
  setPendingArtifactAttachments: (
    refs: ArtifactAttachmentRef[],
    restrictedArtifactIds?: string[],
  ) => void;

  /**
   * SharePoint files queued for the composer, each one an address rather than a copy. Its own slot beside
   * {@link UiState.pendingArtifactAttachments} because the two kinds are different shapes on the wire and
   * a draft can carry both; both entry points outside the composer write here — "Add to chat" in the
   * SharePoint tab (which then navigates to the chat) and the `+` menu's tree dialog — and `useComposer`
   * consumes the slot into chips and clears it. Session-only, never persisted: a queued file is a gesture
   * in flight, not a preference.
   *
   * There is no "private" list alongside it, unlike the artifact carrier: what a SharePoint file is open
   * to is decided in SharePoint, and each member browses the tree on their own connection.
   */
  pendingSharePointAttachments: SharePointAttachmentRef[];
  setPendingSharePointAttachments: (refs: SharePointAttachmentRef[]) => void;

  /** Personal OneDrive files queued for the composer. Session-only and never persisted. */
  pendingOneDriveAttachments: OneDriveAttachmentRef[];
  setPendingOneDriveAttachments: (refs: OneDriveAttachmentRef[]) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

      mobileDrawerOpen: false,
      setMobileDrawerOpen: (mobileDrawerOpen) => set({ mobileDrawerOpen }),

      commandPaletteOpen: false,
      setCommandPaletteOpen: (commandPaletteOpen) =>
        set({ commandPaletteOpen }),
      toggleCommandPalette: () =>
        set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),

      contextPanelOpen: false,
      setContextPanelOpen: (contextPanelOpen) => set({ contextPanelOpen }),

      artifactsViewMode: 'grid',
      setArtifactsViewMode: (artifactsViewMode) => set({ artifactsViewMode }),
      qualitySetupOfferDismissed: false,
      setQualitySetupOfferDismissed: (qualitySetupOfferDismissed) =>
        set({ qualitySetupOfferDismissed }),
      qualityAgentTipsDismissed: false,
      setQualityAgentTipsDismissed: (qualityAgentTipsDismissed) =>
        set({ qualityAgentTipsDismissed }),

      artifactPanelOpen: false,
      activeArtifactId: null,
      artifactPanelWidth: ARTIFACT_PANEL_DEFAULT_WIDTH,
      artifactPanelCloseCount: 0,
      requestedArtifactId: null,
      openArtifactPanel: (activeArtifactId) =>
        set({
          artifactPanelOpen: true,
          activeArtifactId,
          requestedArtifactId: null,
        }),
      openCitedArtifactPanel: (activeArtifactId) =>
        set({
          artifactPanelOpen: true,
          activeArtifactId,
          requestedArtifactId: activeArtifactId,
        }),
      clearRequestedArtifact: () => set({ requestedArtifactId: null }),
      closeArtifactPanel: () =>
        set((s) => ({
          artifactPanelOpen: false,
          artifactPanelCloseCount: s.artifactPanelCloseCount + 1,
          requestedArtifactId: null,
        })),
      collapseArtifactPanel: () => set({ artifactPanelOpen: false }),
      setActiveArtifactId: (activeArtifactId) => set({ activeArtifactId }),
      setArtifactPanelWidth: (rawWidth) =>
        set({ artifactPanelWidth: clampArtifactPanelWidth(rawWidth) }),

      selectedWorkspaceId: null,
      hasHydrated: false,
      lastWorkspaceChangeReason: 'system',
      setSelectedWorkspaceId: (selectedWorkspaceId, reason = 'system') =>
        set({ selectedWorkspaceId, lastWorkspaceChangeReason: reason }),

      pendingFiles: [],
      setPendingFiles: (pendingFiles) => set({ pendingFiles }),

      pendingAttachments: [],
      setPendingAttachments: (pendingAttachments) =>
        set({ pendingAttachments }),

      pendingSkillIds: [],
      setPendingSkillIds: (pendingSkillIds) => set({ pendingSkillIds }),

      pendingConversationId: null,
      setPendingConversationId: (pendingConversationId) =>
        set({ pendingConversationId }),

      pendingMic: false,
      setPendingMic: (pendingMic) => set({ pendingMic }),

      pendingDraft: null,
      setPendingDraft: (pendingDraft) => set({ pendingDraft }),

      composerPrefill: null,
      composerPrefillConversationId: null,
      composerPrefillKeepsDraft: false,
      setComposerPrefill: (composerPrefill, conversationId, options) =>
        set({
          composerPrefill,
          composerPrefillConversationId: conversationId ?? null,
          composerPrefillKeepsDraft: options?.keepDraft === true,
        }),

      composerPrefillRestoresDraft: false,
      setComposerPrefillRestoresDraft: (composerPrefillRestoresDraft) =>
        set({ composerPrefillRestoresDraft }),

      composerPrefillAttachments: [],
      setComposerPrefillAttachments: (composerPrefillAttachments) =>
        set({ composerPrefillAttachments }),

      composerPrefillSkillIds: [],
      setComposerPrefillSkillIds: (composerPrefillSkillIds) =>
        set({ composerPrefillSkillIds }),

      pendingSkill: null,
      setPendingSkill: (pendingSkill) => set({ pendingSkill }),

      pendingSkillsDirectoryId: null,
      setPendingSkillsDirectoryId: (pendingSkillsDirectoryId) =>
        set({ pendingSkillsDirectoryId }),

      pendingArtifactAttachments: [],
      pendingRestrictedArtifactIds: [],
      setPendingArtifactAttachments: (
        pendingArtifactAttachments,
        restrictedArtifactIds,
      ) =>
        set({
          pendingArtifactAttachments,
          pendingRestrictedArtifactIds: restrictedArtifactIds ?? [],
        }),

      pendingSharePointAttachments: [],
      setPendingSharePointAttachments: (pendingSharePointAttachments) =>
        set({ pendingSharePointAttachments }),

      pendingOneDriveAttachments: [],
      setPendingOneDriveAttachments: (pendingOneDriveAttachments) =>
        set({ pendingOneDriveAttachments }),
    }),
    {
      name: 'ct-ui-state',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        selectedWorkspaceId: state.selectedWorkspaceId,
        artifactsViewMode: state.artifactsViewMode,
        qualitySetupOfferDismissed: state.qualitySetupOfferDismissed,
        qualityAgentTipsDismissed: state.qualityAgentTipsDismissed,
        artifactPanelOpen: state.artifactPanelOpen,
        activeArtifactId: state.activeArtifactId,
        artifactPanelWidth: state.artifactPanelWidth,
      }),
    },
  ),
);

/**
 * Post-rehydration fixups. localStorage is synchronous, so zustand/persist has already merged the
 * stored state by the time `create` returns and this line runs.
 *
 * They do NOT belong in `onRehydrateStorage`: that callback fires while `useUiStore` is still being
 * assigned, so touching the store by name there throws a `ReferenceError` that persist swallows —
 * leaving `hasHydrated` false forever and freezing every screen that waits on it.
 *
 * Re-clamp the restored panel width while we are here: the stored value predates any change to the
 * bounds, and a hand-edited or corrupted entry must not be able to squeeze the chat column to nothing.
 */
useUiStore.setState((state) => ({
  hasHydrated: true,
  artifactPanelWidth: clampArtifactPanelWidth(state.artifactPanelWidth),
}));

/** Test-only: reset in-memory state and clear persisted UI chrome. */
export function resetUiStoreForTests(): void {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem('ct-ui-state');
    } catch {
      /* private-mode / embedded contexts */
    }
  }
  useUiStore.setState({
    sidebarCollapsed: false,
    mobileDrawerOpen: false,
    commandPaletteOpen: false,
    contextPanelOpen: false,
    artifactsViewMode: 'grid',
    qualitySetupOfferDismissed: false,
    qualityAgentTipsDismissed: false,
    artifactPanelOpen: false,
    activeArtifactId: null,
    requestedArtifactId: null,
    artifactPanelWidth: ARTIFACT_PANEL_DEFAULT_WIDTH,
    artifactPanelCloseCount: 0,
    selectedWorkspaceId: null,
    hasHydrated: true,
    lastWorkspaceChangeReason: 'system',
    pendingFiles: [],
    pendingAttachments: [],
    pendingConversationId: null,
    pendingMic: false,
    pendingDraft: null,
    composerPrefill: null,
    composerPrefillConversationId: null,
    composerPrefillRestoresDraft: false,
    composerPrefillAttachments: [],
    composerPrefillSkillIds: [],
    pendingSkillIds: [],
    pendingSkill: null,
    pendingSkillsDirectoryId: null,
    pendingArtifactAttachments: [],
    pendingRestrictedArtifactIds: [],
    pendingSharePointAttachments: [],
    pendingOneDriveAttachments: [],
  });
}
